import { useMemo } from 'react';

import { Filters, SortDirection } from '@work4all/models/lib/DataProvider';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

export interface useProjectPickerFilterReturn {
  searchPrefilter: Filters;
  prefilter: Filters;
  sortBy: string;
  sortByDirection: SortDirection;
  useSearchHistory: boolean;
}

export interface useProjectPickerFilterParams {
  businessPartnerId?: number;
  businessPartnerType?: SdObjType;
}

const SEARCH_PREFILTER: Filters = [];

/**
 * Custom hook for creating project prefilter logic based on customer/supplier business partner selection.
 *
 * @param params - Object containing businessPartnerId and businessPartnerType
 * @param params.businessPartnerId - The ID of the selected business partner
 * @param params.businessPartnerType - The type of business partner (SdObjType.KUNDE for customer, SdObjType.LIEFERANT for supplier)
 * @returns Object containing customerFilter, sortBy, sortByDirection, and useSearchHistory
 */
export function useProjectPickerFilter(
  params?: useProjectPickerFilterParams | number
): useProjectPickerFilterReturn {
  // Handle backward compatibility with old signature
  const businessPartnerId =
    typeof params === 'number' ? params : params?.businessPartnerId;
  const businessPartnerType =
    typeof params === 'object' ? params?.businessPartnerType : undefined;

  const filter = useMemo(() => {
    if (!businessPartnerId) return [];

    // Determine if this is a supplier or customer
    const isSupplier = businessPartnerType === SdObjType.LIEFERANT;
    const filterField = isSupplier ? 'supplierId' : 'customerId';

    return [
      {
        $and: [
          {
            $or: [
              {
                [filterField]: {
                  $eq: businessPartnerId,
                },
              },
              {
                [filterField]: { $eq: 0 },
              },
            ],
          },
        ],
      },
    ];
  }, [businessPartnerId, businessPartnerType]);

  const isSupplier = businessPartnerType === SdObjType.LIEFERANT;
  const sortBy = filter.length
    ? isSupplier
      ? 'supplier.id'
      : 'customer.id'
    : 'name';

  const sortByDirection = filter.length
    ? SortDirection.DESCENDING
    : SortDirection.ASCENDING;

  const useSearchHistory = !businessPartnerId;

  return {
    searchPrefilter: SEARCH_PREFILTER,
    prefilter: filter,
    sortBy,
    sortByDirection,
    useSearchHistory,
  };
}
